import { useMemo, useState, type KeyboardEvent } from 'react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import {
  Button,
  ConditionViewerItem,
  ConditionViewerTerm,
  Icon,
  InlineStack,
  Stack,
  Tooltip,
} from '@customerio/pluma-components/react';
import { useAttributeValues } from '@/services/db/attribute/queries';
import { isUnaryFilterOperator, type AttributeFilterState } from '../attribute-filter-state';
import { attributeFilterErrorMessage, attributeFilterErrors } from '../codecs/legacy-attribute-filter';
import { groupsForFamilies as peopleGroupsForFamilies } from '../codecs/attribute-filter';
import type { GroupableFamily } from '../operator-groups';
import { ProfileAttributeNamePicker, useProfileAttributeNames } from '../rows/profile-attribute-name-picker';
import { OperatorValue, OperatorValueControls, type OperatorValueProps } from './operator-value/operator-value';
import { CONDITION_CONTROL_GAP, toValueOptions, type ConditionControlSize } from './operator-value/types';

/**
 * One profile-attribute condition for a people search by filters.
 *
 * While editing it is the people search's attribute row: the same name
 * picker, and only the operator families the caller passes, with an Apply
 * that runs the search. The customers endpoint takes few operators: in
 * production a `contains` or `gt` row there is a 400. A caller resolves its
 * families with `useAttributeFilterOperatorFamilies`.
 *
 * Once applied it reads back as a viewer term, which can be opened to edit
 * again or removed.
 */

type AttributeFilterConditionProps = {
  workspaceId: number;
  /** The condition as last applied, or the blank one before any has been. */
  condition: AttributeFilterState;
  isEditing: boolean;
  isDisabled: boolean;
  /** The operator families the dropdown offers, and the applied condition's operator is named from. */
  operatorFamilies: readonly GroupableFamily[];
  onApply: (condition: AttributeFilterState) => void;
  onEdit: () => void;
  onDelete: () => void;
  /** The size of its controls: small beside a compact search, medium where the search is full size. */
  size?: ConditionControlSize;
  /**
   * How the editor lays out its controls: side by side in a wrapping row, or
   * stacked full width, one under another, for a search in a narrow pane.
   */
  direction?: AttributeFilterDirection;
  /**
   * Leaves the editor for the applied condition, unchanged. Without it the
   * cancel button stays disabled, for a search with no applied condition to
   * go back to.
   */
  onCancel?: () => void;
};

type AttributeFilterDirection = 'row' | 'column';

export function AttributeFilterCondition({
  workspaceId,
  condition,
  isEditing,
  isDisabled,
  operatorFamilies,
  onApply,
  onEdit,
  onDelete,
  size = 'sm',
  direction = 'row',
  onCancel,
}: AttributeFilterConditionProps) {
  if (isEditing) {
    return (
      <AttributeFilterEditor
        workspaceId={workspaceId}
        initialCondition={condition}
        isDisabled={isDisabled}
        operatorFamilies={operatorFamilies}
        onApply={onApply}
        onCancel={onCancel}
        size={size}
        direction={direction}
      />
    );
  }

  return (
    <InlineStack gap="050" alignBlock="start">
      <AppliedCondition
        condition={condition}
        operatorFamilies={operatorFamilies}
        isDisabled={isDisabled}
        onEdit={onEdit}
      />
      <Button
        aria-label="Remove condition"
        icon="close"
        isIconOnly
        isCritical
        variant="tertiary"
        size={size}
        isDisabled={isDisabled}
        onClick={onDelete}
      />
    </InlineStack>
  );
}

/** The operator as the dropdown named it when it was chosen. */
function operatorLabel(condition: AttributeFilterState, operatorFamilies: readonly GroupableFamily[]): string {
  const options = peopleGroupsForFamilies(operatorFamilies, condition.field).flatMap((group) => group.options);
  return options.find((option) => option.value === condition.operator)?.label ?? condition.operator;
}

type AppliedConditionProps = {
  condition: AttributeFilterState;
  operatorFamilies: readonly GroupableFamily[];
  isDisabled: boolean;
  onEdit: () => void;
};

/**
 * The condition read back as a sentence, clicked to edit it again.
 *
 * The viewer item is itself the button, by role rather than as a <button>:
 * Pluma draws a <div> inside it, which a <button> may not hold.
 */
function AppliedCondition({ condition, operatorFamilies, isDisabled, onEdit }: AppliedConditionProps) {
  const edit = () => {
    if (!isDisabled) {
      onEdit();
    }
  };
  return (
    <ConditionViewerItem
      role="button"
      tabIndex={0}
      aria-disabled={isDisabled}
      cursor={isDisabled ? undefined : 'pointer'}
      icon="person"
      onClick={edit}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          edit();
        }
      }}
    >
      <ConditionViewerTerm>{condition.field}</ConditionViewerTerm> {operatorLabel(condition, operatorFamilies)}
      {isUnaryFilterOperator(condition.operator) ? null : (
        <>
          {' '}
          {/* The value is subscriber data; keep it out of session replay. */}
          <ConditionViewerTerm className="fs-mask">{condition.value}</ConditionViewerTerm>
        </>
      )}
    </ConditionViewerItem>
  );
}

type AttributeFilterEditorProps = {
  workspaceId: number;
  initialCondition: AttributeFilterState;
  isDisabled: boolean;
  operatorFamilies: readonly GroupableFamily[];
  onApply: (condition: AttributeFilterState) => void;
  onCancel: (() => void) | undefined;
  size: ConditionControlSize;
  direction: AttributeFilterDirection;
};

function AttributeFilterEditor({
  workspaceId,
  initialCondition,
  isDisabled,
  operatorFamilies,
  onApply,
  onCancel,
  size,
  direction,
}: AttributeFilterEditorProps) {
  const { hasPermission } = usePermissions();
  const canViewAttributeValues = hasPermission('journeys/attribute_data/view');
  const [draft, setDraft] = useState(initialCondition);

  // The workspace the caller resolved, not the URL's: a caller can render this
  // under `/workspaces/last/...`, where the URL holds no numeric id.
  const names = useProfileAttributeNames(draft.field, workspaceId);
  // A blank field makes no request, which is how a viewer who may not read
  // attribute data is asked nothing on their behalf.
  const values = useAttributeValues(workspaceId, canViewAttributeValues ? draft.field : '');
  const valueOptions = useMemo(() => toValueOptions(values.data), [values.data]);

  const errors = attributeFilterErrors(draft);
  const errorMessage = attributeFilterErrorMessage(errors);

  // Lower-case placeholders, as the people search's attribute editor has them.
  const namePicker = (
    <ProfileAttributeNamePicker
      placeholder="profile attribute"
      field={draft.field}
      isDisabled={isDisabled}
      names={names}
      onFieldChange={(field) => setDraft((current) => ({ ...current, field }))}
      onTouched={() => {}}
      size={size}
    />
  );
  const operatorValueProps: OperatorValueProps = {
    field: draft.field,
    operator: draft.operator,
    value: draft.value,
    value2: draft.value2,
    operatorGroups: peopleGroupsForFamilies(operatorFamilies, draft.field),
    valueOptions,
    isLoadingValueOptions: values.isFetching,
    disabled: isDisabled,
    valuePlaceholder: 'value',
    onChange: (patch) => setDraft((current) => ({ ...current, ...patch })),
    size,
  };
  // Apply and cancel, 8px apart: each a button of the row's size holding its icon.
  // Apply is disabled while the condition is incomplete, and the tooltip
  // beside it names what is missing, so the flip is what says which parts are
  // required. Cancel is disabled while there is no applied condition to go
  // back to.
  const buttons = (
    <InlineStack gap="100">
      <Tooltip isDisabled={errors.length === 0} content={errorMessage} isCustom>
        {(context) => (
          <Button
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            aria-label="Apply condition"
            variant="primary"
            size={size}
            isDisabled={isDisabled || errors.length > 0}
            onClick={() => onApply(draft)}
          >
            <Icon name="check" />
          </Button>
        )}
      </Tooltip>
      <Button
        aria-label="Cancel condition"
        variant="tertiary"
        size={size}
        isDisabled={isDisabled || onCancel == null}
        onClick={onCancel}
      >
        <Icon name="close" />
      </Button>
    </InlineStack>
  );

  switch (direction) {
    case 'row':
      return (
        // Top-aligned: every control is the same height, and the icon-only
        // buttons have no text baseline to line up by.
        <InlineStack gap={CONDITION_CONTROL_GAP[size]} alignBlock="start" wrap>
          {namePicker}
          <OperatorValue {...operatorValueProps} />
          {buttons}
        </InlineStack>
      );
    case 'column':
      // Each control fills the width, one under another, with the buttons
      // at the start of their own line.
      return (
        <Stack gap={CONDITION_CONTROL_GAP[size]}>
          {namePicker}
          <OperatorValueControls {...operatorValueProps} />
          {buttons}
        </Stack>
      );
    default: {
      const _exhaustive: never = direction;
      return _exhaustive;
    }
  }
}
